import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Handle,
  Position,
  MarkerType,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useNodesInitialized,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Server, Boxes } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { integrationsApi, type Topology, type TopologyNode } from '@/api/integrationsApi'
import { useTenant } from '@/providers/TenantProvider'

// ─── Layout ──────────────────────────────────────────────────────────────────
// The platform is the center. The middleware hub sits to its LEFT, fed by a
// far-left column of middleware-routed integrations (instruments → hub →
// platform). Direct (no-middleware) integrations surround the platform on its
// top, bottom and right. `dock` is the side of a node its connector exits from.
type NodeDock = 'left' | 'right' | 'top' | 'bottom'
type NodePosition = { x: number; y: number; dock?: NodeDock }

function computeLayout(topology: Topology): Record<string, NodePosition> {
  const pos: Record<string, NodePosition> = {}
  const integrations = topology.nodes.filter((n) => n.type === 'integration')
  const hub = topology.nodes.find((n) => n.type === 'middleware')
  const platform = topology.nodes.find((n) => n.type === 'platform')

  const X_INSTR = 0 // far-left middleware instruments column
  const X_HUB = 380 // hub, immediately left of the platform
  const X_PLAT = 780 // platform (center; top/bottom rows are centered here too)
  const X_RIGHT = 1180 // direct integrations docked to the platform's right
  const ROW_GAP = 110 // vertical spacing of stacked nodes
  const COL_GAP = 280 // horizontal spacing of nodes in the top/bottom rows
  const TOP_Y = -260 // top row, above the platform
  const BOTTOM_Y = 260 // bottom row, below the platform

  const viaHub = integrations.filter((n) => n.viaMiddleware)
  const direct = integrations.filter((n) => !n.viaMiddleware)

  // Center a list of values around 0 with a fixed gap.
  const centered = (count: number, i: number, gap: number) => (i - (count - 1) / 2) * gap

  // 1. Middleware instruments — far-left column, all feeding the hub on their right.
  viaHub.forEach((n, i) => {
    pos[n.id] = { x: X_INSTR, y: centered(viaHub.length, i, ROW_GAP), dock: 'right' }
  })

  // 2. Hub (left of platform) and platform share the baseline row.
  if (hub) pos[hub.id] = { x: X_HUB, y: 0 }
  if (platform) pos[platform.id] = { x: X_PLAT, y: 0 }

  // 3. Direct integrations — spread around the platform: top, right, bottom.
  const top: TopologyNode[] = []
  const right: TopologyNode[] = []
  const bottom: TopologyNode[] = []
  direct.forEach((n, i) => {
    if (i % 3 === 0) top.push(n)
    else if (i % 3 === 1) right.push(n)
    else bottom.push(n)
  })
  top.forEach((n, j) => {
    pos[n.id] = { x: X_PLAT + centered(top.length, j, COL_GAP), y: TOP_Y, dock: 'bottom' }
  })
  bottom.forEach((n, j) => {
    pos[n.id] = { x: X_PLAT + centered(bottom.length, j, COL_GAP), y: BOTTOM_Y, dock: 'top' }
  })
  right.forEach((n, k) => {
    pos[n.id] = { x: X_RIGHT, y: centered(right.length, k, ROW_GAP), dock: 'left' }
  })

  return pos
}

const DOCK_POSITION: Record<NodeDock, Position> = {
  left: Position.Left,
  right: Position.Right,
  top: Position.Top,
  bottom: Position.Bottom,
}

// The platform handle side a docked integration connects to (opposite its dock).
const DOCK_TO_PLATFORM_SIDE: Record<NodeDock, 'left' | 'right' | 'top' | 'bottom'> = {
  right: 'left',
  left: 'right',
  bottom: 'top',
  top: 'bottom',
}

// ─── Node renderers ────────────────────────────────────────────────────────────
type IntegrationNodeData = TopologyNode & {
  onToggle?: (id: string, next: boolean) => void
  dock?: NodeDock
}

function IntegrationNode({ id, data }: NodeProps<Node<IntegrationNodeData>>) {
  const active = data.status === 'active'
  // Source + target overlap on the docking side so either edge orientation
  // (inbound vs. outbound) routes through the same connection point.
  const facing = DOCK_POSITION[data.dock ?? 'right']
  return (
    <div
      className={`rounded-lg border bg-white shadow-sm px-3 py-2 w-56 ${
        active ? 'border-emerald-400' : 'border-gray-200'
      }`}
    >
      <Handle type="target" id="tgt" position={facing} className="!bg-gray-300" />
      <Handle type="source" id="src" position={facing} className="!bg-gray-300" />
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{data.label}</div>
          {data.sublabel && (
            <div className="text-[11px] text-muted-foreground truncate">{data.sublabel}</div>
          )}
        </div>
        {/* stopPropagation so toggling status doesn't also trigger node navigation */}
        <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <Switch
            checked={active}
            onCheckedChange={(next) => data.onToggle?.(id, next)}
            className="data-[state=checked]:bg-emerald-500"
          />
        </div>
      </div>
      {data.protocol && (
        <div className="mt-1 text-[10px] uppercase tracking-wide text-muted-foreground">
          {data.protocol}
        </div>
      )}
      <div className="mt-1 text-[10px] text-sky-600">Open field mapping →</div>
    </div>
  )
}

function MiddlewareNode({ data }: NodeProps<Node<IntegrationNodeData>>) {
  // UI-only toggle for now — local state, no backend wiring.
  const [enabled, setEnabled] = useState(true)
  return (
    <div className="rounded-lg border-2 border-sky-300 bg-sky-50 shadow-sm px-3 py-3 w-56 text-center">
      {/* Instruments feed the hub on the left; the platform hangs off the right. */}
      <Handle type="target" id="hub-left-in" position={Position.Left} className="!bg-sky-300" />
      <Handle type="source" id="hub-left-out" position={Position.Left} className="!bg-sky-300" />
      <Handle type="source" id="hub-right-out" position={Position.Right} className="!bg-sky-300" />
      <Handle type="target" id="hub-right-in" position={Position.Right} className="!bg-sky-300" />
      <div className="flex items-center justify-center gap-1.5">
        <Boxes className="h-4 w-4 text-sky-600 shrink-0" />
        <span className="text-sm font-semibold truncate">{data.label}</span>
      </div>
      {data.sublabel && (
        <div className="text-[11px] text-sky-700 leading-tight">{data.sublabel}</div>
      )}
      <div
        className="mt-1 flex items-center justify-center gap-2"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <span className="text-[11px] text-muted-foreground">Enabled</span>
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          className="data-[state=checked]:bg-emerald-500"
        />
      </div>
    </div>
  )
}

function PlatformNode({ data }: NodeProps<Node<IntegrationNodeData>>) {
  return (
    <div className="rounded-xl bg-[#1e4e8c] text-white shadow-md px-5 py-6 w-56 text-center">
      {/* The hub feeds the left side; direct spokes attach on every side. Each side
          carries an inbound (target) and outbound (source) handle. */}
      <Handle type="target" position={Position.Left} id="pf-left-in" className="!bg-white/60" />
      <Handle type="source" position={Position.Left} id="pf-left-out" className="!bg-white/60" />
      <Handle type="target" position={Position.Right} id="pf-right-in" className="!bg-white/60" />
      <Handle type="source" position={Position.Right} id="pf-right-out" className="!bg-white/60" />
      <Handle type="target" position={Position.Top} id="pf-top-in" className="!bg-white/60" />
      <Handle type="source" position={Position.Top} id="pf-top-out" className="!bg-white/60" />
      <Handle type="target" position={Position.Bottom} id="pf-bottom-in" className="!bg-white/60" />
      <Handle type="source" position={Position.Bottom} id="pf-bottom-out" className="!bg-white/60" />
      <Server className="h-6 w-6 mx-auto" />
      <div className="text-base font-bold mt-1">{data.label}</div>
      {data.sublabel && <div className="text-[11px] text-white/70">{data.sublabel}</div>}
    </div>
  )
}

const nodeTypes = {
  integration: IntegrationNode,
  middleware: MiddlewareNode,
  platform: PlatformNode,
}

function TopologyFlow() {
  const navigate = useNavigate()
  const { selectedTenantId } = useTenant()
  const { fitView } = useReactFlow()
  const nodesInitialized = useNodesInitialized()
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['integration-topology', selectedTenantId],
    queryFn: () => integrationsApi.getTopology(selectedTenantId as string),
    enabled: !!selectedTenantId,
  })

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<IntegrationNodeData>>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])

  const handleToggle = useCallback(
    (id: string, next: boolean) => {
      if (!selectedTenantId) return
      // optimistic
      setNodes((ns) =>
        ns.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, status: next ? 'active' : 'inactive' } } : n,
        ),
      )
      integrationsApi
        .update(selectedTenantId, id, { status: next ? 'active' : 'inactive' })
        .catch(() => refetch())
    },
    [selectedTenantId, setNodes, refetch],
  )

  const positions = useMemo(() => (data ? computeLayout(data) : {}), [data])

  useEffect(() => {
    if (!data) return
    setNodes(
      data.nodes.map((n) => ({
        id: n.id,
        type: n.type,
        position: { x: positions[n.id]?.x ?? 0, y: positions[n.id]?.y ?? 0 },
        data: { ...n, dock: positions[n.id]?.dock, onToggle: handleToggle },
        draggable: false,
      })),
    )

    // Pick the source/target handles for each edge so the elbow connectors enter
    // and leave nodes on the geometrically correct side.
    const typeById = new Map(data.nodes.map((n) => [n.id, n.type]))
    setEdges(
      data.edges.map((e) => {
        const sourceType = typeById.get(e.source)
        const targetType = typeById.get(e.target)
        const touchesPlatform = sourceType === 'platform' || targetType === 'platform'
        const touchesIntegration = sourceType === 'integration' || targetType === 'integration'
        // A direct edge wires an integration straight to the platform (no hub).
        const isDirect = touchesPlatform && touchesIntegration

        let sourceHandle: string | undefined
        let targetHandle: string | undefined
        if (touchesPlatform && !touchesIntegration) {
          // Middleware hub → platform (horizontal, hub sits to the left).
          if (sourceType === 'middleware') {
            sourceHandle = 'hub-right-out'
            targetHandle = 'pf-left-in'
          } else {
            sourceHandle = 'pf-left-out'
            targetHandle = 'hub-right-in'
          }
        } else {
          // Integration ↔ (hub or platform). The integration's src/tgt sit on its
          // docking side; the other end depends on whether it's the hub or platform.
          const integrationIsSource = sourceType === 'integration'
          const integrationId = integrationIsSource ? e.source : e.target
          const dock = positions[integrationId]?.dock ?? 'right'
          let otherIn: string
          let otherOut: string
          if (isDirect) {
            const side = DOCK_TO_PLATFORM_SIDE[dock]
            otherIn = `pf-${side}-in`
            otherOut = `pf-${side}-out`
          } else {
            otherIn = 'hub-left-in'
            otherOut = 'hub-left-out'
          }
          if (integrationIsSource) {
            sourceHandle = 'src'
            targetHandle = otherIn
          } else {
            sourceHandle = otherOut
            targetHandle = 'tgt'
          }
        }
        return {
          id: e.id,
          source: e.source,
          target: e.target,
          sourceHandle,
          targetHandle,
          // Angled elbow connectors (orthogonal) instead of curves.
          type: 'smoothstep',
          markerEnd: { type: MarkerType.ArrowClosed },
          markerStart:
            e.direction === 'bidirectional' ? { type: MarkerType.ArrowClosed } : undefined,
          // Direct connections are drawn dashed to read as "no middleware".
          style: isDirect
            ? { stroke: '#94a3b8', strokeDasharray: '6 4' }
            : { stroke: '#94a3b8' },
        }
      }),
    )
  }, [data, positions, handleToggle, setNodes, setEdges])

  // Re-fit the viewport whenever a new tenant's nodes have been measured, so the
  // zoom/pan always frames the whole diagram (the `fitView` prop only runs once on
  // mount). `useNodesInitialized` flips false→true each time the node set changes.
  useEffect(() => {
    if (nodesInitialized && nodes.length > 0) {
      fitView({ padding: 0.2, duration: 200 })
    }
  }, [nodesInitialized, nodes.length, selectedTenantId, fitView])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="h-[640px] w-full rounded-lg border bg-muted/10">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={(_e, node) => {
          // Clicking an integration node opens its detail page (fetch + results).
          if (node.type === 'integration') navigate(`/integrations/${node.id}`)
        }}
        fitView
        proOptions={{ hideAttribution: true }}
        // Fixed, read-only presentation — no dragging, connecting, selecting, or panning/zooming.
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={false}
        panOnScroll={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
      >
        <Background />
      </ReactFlow>
    </div>
  )
}

// `useReactFlow`/`useNodesInitialized` require the React Flow context, so the diagram
// is wrapped in its own provider.
export function TopologyDiagram() {
  return (
    <ReactFlowProvider>
      <TopologyFlow />
    </ReactFlowProvider>
  )
}
